/* eslint-disable camelcase */
import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE, ENUMS } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

const RoleTreeModal = NiceModal.create(({ id, mode, modalName }) => {
	const isUpdateMode = mode === 'edit';

	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();

	const { closeModal } = useModalRouter();

	const handleCloseModal = () => {
		closeModal();
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					langResource: yup.string().nullable().required(),
					OTO_V2_Roles_ID: yup.number().nullable().required(),
					helperRoleIDs: yup.array().nullable(),
					parentID: yup.number().nullable(),
					type: yup.string().nullable().required()
				})
				.required(),
		[]
	);

	const fillForm = useCallback(async (id) => {
		if (!isUpdateMode) {
			return {
				langResource: null,
				OTO_V2_Roles_ID: null,
				helperRoleIDs: null,
				parentID: id ?? null,
				type: 'page'
			};
		}

		try {
			const response = await axiosPrivate.post(ADMIN.ROLE_TREE.GET_ROLE_TREE_RECORD, {
				ID: id
			});

			const { data, status } = response;

			if (status !== 200) {
				showError();
				handleCloseModal();
				return;
			}

			return {
				langResource: data?.langResource ?? null,
				OTO_V2_Roles_ID: data?.OTO_V2_Roles_ID ?? null,
				helperRoleIDs: data?.helperRoleIDs ? JSON.parse(data?.helperRoleIDs) : null,
				parentID: data?.parentID ?? null,
				type: data?.type ?? null
			};
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: async () => await fillForm(id)
	});

	const {
		setValue,
		watch,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		dispatch(setLoading(true));
		await axiosPrivate
			.post(isUpdateMode ? ADMIN.ROLE_TREE.UPDATE_ROLE_TREE_RECORD : ADMIN.ROLE_TREE.ADD_ROLE_TREE_RECORD, {
				ID: id,
				...data
			})
			.then(() => {
				showSuccess();
				dispatchEvent(new Event('updateRoleTree'));
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		dispatch(setLoading(false));
		handleCloseModal();
	};

	const roleAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'path', width: 1000 }
		],
		[]
	);

	const roleTreeAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'langResource', width: 1000 }
		],
		[]
	);

	const defaultColSpan = {
		md: 12
	};

	return (
		<Modal maxWidth="medium" name={modalName} fullwidth className="modal-roleTreeModal">
			<ModalTitle>
				<Typography variant="p1">{isUpdateMode ? 'updateRoleTreeRecord' : 'addRoleTreeRecord'}</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="name" required>
						<StringInput name="langResource" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="roleID" required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.V2ROLES}
							name="v2Roles"
							value={watch('OTO_V2_Roles_ID')}
							setValue={(value) => {
								setValue('OTO_V2_Roles_ID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['path']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="Helper Roles">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.V2ROLES}
							name="helperRoleIDs"
							value={watch('helperRoleIDs')}
							setValue={(value) => {
								setValue('helperRoleIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['path']}
							multipleSelection
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="Parent Role">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLE_TREE}
							name="parentID"
							value={watch('parentID')}
							setValue={(value) => {
								setValue('parentID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							columns={roleTreeAutocompleteColumns}
							selectionLabelAccessor={['langResource']}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="type" required>
						<EnumSelect
							enumName={ENUMS.ROLE_TREE_TYPE}
							name="type"
							value={watch('type')}
							setValue={(value) => {
								setValue('type', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							searchable={false}
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label={isUpdateMode ? 'save' : 'add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default RoleTreeModal;
